/**
 * Fidelización (SK Miembros equivalent) — réplica del backoffice de Skeleton.
 *   3 tabs: Tiers | Códigos | Miembros
 *   4 KPIs arriba: Tiers activos / Miembros / Códigos generados / Canjeados
 *
 * Plugin gate del backend: si tenant no tiene metadata.modules.loyalty=true,
 * la API responde 404 y mostramos un empty-state explicando cómo habilitar.
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, Input, Label, Button, PageHeader } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import {
  Plus, Award, Users, Ticket, Download, Filter, Copy, Check, X,
  Sparkles, Clock, ShieldCheck, Calendar, Trash2, Edit, ChevronDown,
} from 'lucide-react'

export const Route = createFileRoute('/store/$id/loyalty')({
  component: StoreLoyaltyPage,
})

// ============================================================
// Types (matchean DTOs del service)
// ============================================================

type BenefitType = 'discount_percent' | 'countdown_reduction' | 'free_shipping' | 'early_access' | 'category_discount'

interface Benefit {
  id: string
  type: BenefitType
  config: Record<string, unknown>
  sortOrder: number
  isActive: boolean
}

interface Tier {
  id: string
  name: string
  slug: string
  description: string | null
  color: string
  sortOrder: number
  defaultDurationDays: number | null
  stackableWithCoupons: boolean
  isActive: boolean
  benefits: Benefit[]
}

interface CodeRow {
  id: string
  code: string
  tierId: string
  tierName: string
  batchId: string | null
  expiresAt: string | null
  redeemedByCustomerId: string | null
  redeemedByEmail: string | null
  redeemedAt: string | null
  createdAt: string
  status: 'active' | 'redeemed' | 'expired'
}

interface Member {
  id: string
  customerId: string
  customerEmail: string | null
  customerName: string | null
  tierId: string
  tierName: string
  tierColor: string
  codeId: string | null
  activatedAt: string
  expiresAt: string | null
  isActive: boolean
}

interface Stats {
  activeTiers: number
  activeMembers: number
  totalCodes: number
  redeemedCodes: number
}

const BENEFIT_META: Record<BenefitType, { label: string; icon: React.ReactNode; describe: (c: Record<string, unknown>) => string; defaultConfig: Record<string, unknown> }> = {
  discount_percent: {
    label: 'Descuento % en checkout', icon: <Sparkles className="h-3.5 w-3.5" />,
    describe: (c) => `${c.value ?? '?'}% off al pagar`, defaultConfig: { value: 10 },
  },
  countdown_reduction: {
    label: 'Reducir countdown', icon: <Clock className="h-3.5 w-3.5" />,
    describe: (c) => c.hours ? `${c.hours}h menos` : c.percent ? `${c.percent}% menos` : '?',
    defaultConfig: { hours: 24 },
  },
  free_shipping: {
    label: 'Envío gratis', icon: <ShieldCheck className="h-3.5 w-3.5" />,
    describe: () => 'sin costo de envío', defaultConfig: {},
  },
  early_access: {
    label: 'Acceso anticipado', icon: <Calendar className="h-3.5 w-3.5" />,
    describe: (c) => `${c.hoursBeforeDrop ?? '?'}h antes`, defaultConfig: { hoursBeforeDrop: 12 },
  },
  category_discount: {
    label: 'Descuento en categoría', icon: <Sparkles className="h-3.5 w-3.5" />,
    describe: (c) => `${c.value ?? '?'}% en categoría`, defaultConfig: { categoryId: '', value: 15 },
  },
}

function fmtDate(d: string | null): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: '2-digit' })
}

// ============================================================
// Página
// ============================================================

function StoreLoyaltyPage() {
  const { activeTenantId } = useAuth()
  const qc = useQueryClient()
  const [tab, setTab] = useState<'tiers' | 'codes' | 'members'>('tiers')
  const [tierDialog, setTierDialog] = useState<{ mode: 'create' | 'edit'; tier?: Tier } | null>(null)
  const [bulkDialog, setBulkDialog] = useState(false)
  const [codeFilter, setCodeFilter] = useState<{ tierId: string; status: 'all' | 'active' | 'redeemed' | 'expired' }>({ tierId: '', status: 'all' })

  const tiersQ = useQuery({
    queryKey: ['loyalty-tiers'],
    queryFn: () => tenantApi.get<{ tiers: Tier[]; stats: Stats }>('/api/v1/loyalty/tiers'),
    enabled: !!activeTenantId,
    retry: false,
  })

  const codesQ = useQuery({
    queryKey: ['loyalty-codes', codeFilter],
    queryFn: () => {
      const params = new URLSearchParams()
      if (codeFilter.tierId) params.set('tierId', codeFilter.tierId)
      if (codeFilter.status !== 'all') params.set('status', codeFilter.status)
      return tenantApi.get<{ codes: CodeRow[]; total: number }>(`/api/v1/loyalty/codes?${params}`)
    },
    enabled: !!activeTenantId && tab === 'codes',
  })

  const membersQ = useQuery({
    queryKey: ['loyalty-members'],
    queryFn: () => tenantApi.get<{ members: Member[]; total: number }>('/api/v1/loyalty/members'),
    enabled: !!activeTenantId && tab === 'members',
  })

  const deleteTierMut = useMutation({
    mutationFn: (id: string) => tenantApi.del(`/api/v1/loyalty/tiers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['loyalty-tiers'] }),
  })
  const deleteCodeMut = useMutation({
    mutationFn: (id: string) => tenantApi.del(`/api/v1/loyalty/codes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['loyalty-codes'] }),
  })
  const revokeMut = useMutation({
    mutationFn: (customerId: string) => tenantApi.del(`/api/v1/loyalty/members/${customerId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['loyalty-members'] }),
  })

  // ──────────── Plugin gate handling: 404 = no habilitado ────────────
  if (tiersQ.isError) {
    return (
      <>
        <PageHeader title="Fidelización" description="Programa de membresías SK Miembros" />
        <Card className="p-12 text-center max-w-2xl mx-auto mt-8">
          <Award className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Módulo no habilitado</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
            El módulo de fidelización requiere activación por tenant. Contactá a la
            plataforma para habilitarlo en tu tienda.
          </p>
          <code className="text-xs bg-[hsl(var(--muted))] px-3 py-1.5 rounded font-mono">
            tenants.metadata.modules.loyalty = true
          </code>
        </Card>
      </>
    )
  }

  const tiers = tiersQ.data?.tiers ?? []
  const stats = tiersQ.data?.stats ?? { activeTiers: 0, activeMembers: 0, totalCodes: 0, redeemedCodes: 0 }
  const codes = codesQ.data?.codes ?? []
  const members = membersQ.data?.members ?? []

  async function handleExportCodes() {
    const params = new URLSearchParams({ format: 'csv' })
    if (codeFilter.tierId) params.set('tierId', codeFilter.tierId)
    if (codeFilter.status !== 'all') params.set('status', codeFilter.status)
    const csv = await tenantApi.getRaw(`/api/v1/loyalty/codes?${params}`)
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `loyalty-codes-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <PageHeader
        title="Fidelización"
        description="Tiers de membresía + códigos single-use + beneficios"
        action={
          tab === 'tiers' ? (
            <Button size="sm" onClick={() => setTierDialog({ mode: 'create' })}>
              <Plus className="h-4 w-4 mr-2" />Nuevo tier
            </Button>
          ) : tab === 'codes' ? (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleExportCodes}>
                <Download className="h-4 w-4 mr-2" />CSV
              </Button>
              <Button size="sm" onClick={() => setBulkDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />Generar códigos
              </Button>
            </div>
          ) : null
        }
      />

      <div className="p-8 space-y-6">

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Tiers activos" value={stats.activeTiers} icon={<Award className="h-4 w-4 text-[hsl(var(--primary))]" />} />
        <KpiCard label="Miembros" value={stats.activeMembers} icon={<Users className="h-4 w-4 text-blue-600" />} />
        <KpiCard label="Códigos generados" value={stats.totalCodes} icon={<Ticket className="h-4 w-4 text-purple-600" />} />
        <KpiCard label="Canjeados" value={stats.redeemedCodes} icon={<Check className="h-4 w-4 text-green-600" />} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        <TabButton active={tab === 'tiers'} onClick={() => setTab('tiers')} icon={<Award className="h-4 w-4" />} label="Tiers" count={tiers.length} />
        <TabButton active={tab === 'codes'} onClick={() => setTab('codes')} icon={<Ticket className="h-4 w-4" />} label="Códigos" count={stats.totalCodes} />
        <TabButton active={tab === 'members'} onClick={() => setTab('members')} icon={<Users className="h-4 w-4" />} label="Miembros" count={members.length} />
      </div>

      {/* Tab: Tiers */}
      {tab === 'tiers' && (
        tiers.length === 0 ? (
          <EmptyState icon={<Award className="h-10 w-10 text-muted-foreground" />}
            title="Aún no creaste tiers"
            description="Los tiers definen la membresía y los beneficios que reciben los clientes que canjean un código."
            action={<Button size="sm" onClick={() => setTierDialog({ mode: 'create' })}><Plus className="h-4 w-4 mr-2" />Crear primer tier</Button>} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tiers.map((t) => (
              <TierCard key={t.id} tier={t}
                onEdit={() => setTierDialog({ mode: 'edit', tier: t })}
                onDelete={() => {
                  if (confirm(`¿Eliminar tier "${t.name}"?`)) deleteTierMut.mutate(t.id)
                }} />
            ))}
          </div>
        )
      )}

      {/* Tab: Códigos */}
      {tab === 'codes' && (
        <>
          <div className="flex items-center gap-3 mb-4 flex-wrap bg-white rounded-lg border border-border p-3">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <select value={codeFilter.tierId} onChange={(e) => setCodeFilter((f) => ({ ...f, tierId: e.target.value }))}
              className="text-sm border border-border rounded px-2 py-1.5 bg-white focus:outline-none">
              <option value="">Todos los tiers</option>
              {tiers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select value={codeFilter.status} onChange={(e) => setCodeFilter((f) => ({ ...f, status: e.target.value as typeof codeFilter.status }))}
              className="text-sm border border-border rounded px-2 py-1.5 bg-white focus:outline-none">
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="redeemed">Canjeados</option>
              <option value="expired">Expirados</option>
            </select>
            <span className="text-xs text-muted-foreground ml-auto">{codes.length} resultados</span>
          </div>

          {codes.length === 0 ? (
            <EmptyState icon={<Ticket className="h-10 w-10 text-muted-foreground" />}
              title="Sin códigos en este filtro"
              description="Generá un lote de códigos para empezar."
              action={tiers.length > 0 ? <Button size="sm" onClick={() => setBulkDialog(true)}><Plus className="h-4 w-4 mr-2" />Generar códigos</Button> : null} />
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[hsl(var(--muted))] border-b border-border text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-3">Código</th>
                      <th className="text-left px-4 py-3">Tier</th>
                      <th className="text-left px-4 py-3">Estado</th>
                      <th className="text-left px-4 py-3">Expira</th>
                      <th className="text-left px-4 py-3">Canjeado por</th>
                      <th className="text-right px-4 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {codes.map((c) => (
                      <CodeRowComp key={c.id} code={c}
                        onDelete={() => { if (confirm(`¿Eliminar "${c.code}"?`)) deleteCodeMut.mutate(c.id) }} />
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Tab: Miembros */}
      {tab === 'members' && (
        members.length === 0 ? (
          <EmptyState icon={<Users className="h-10 w-10 text-muted-foreground" />}
            title="Sin miembros aún"
            description="Cuando un cliente canjee un código, aparecerá acá." />
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--muted))] border-b border-border text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-3">Cliente</th>
                    <th className="text-left px-4 py-3">Tier</th>
                    <th className="text-left px-4 py-3">Activado</th>
                    <th className="text-left px-4 py-3">Vence</th>
                    <th className="text-right px-4 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {members.map((m) => (
                    <tr key={m.id} className="hover:bg-[hsl(var(--muted))]">
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground">{m.customerName ?? m.customerEmail}</p>
                        {m.customerName && m.customerEmail && <p className="text-xs text-muted-foreground">{m.customerEmail}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: m.tierColor + '20', color: m.tierColor }}>
                          <span className="size-1.5 rounded-full" style={{ backgroundColor: m.tierColor }} />
                          {m.tierName}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{fmtDate(m.activatedAt)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{m.expiresAt ? fmtDate(m.expiresAt) : <span className="text-xs text-muted-foreground">Eterno</span>}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => { if (confirm(`¿Revocar ${m.tierName} de ${m.customerEmail}?`)) revokeMut.mutate(m.customerId) }}
                          className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1">
                          <Trash2 className="h-3 w-3" />Revocar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      )}

      </div>

      {tierDialog && (
        <TierFormDialog mode={tierDialog.mode} initialTier={tierDialog.tier}
          onClose={() => setTierDialog(null)}
          onSaved={() => { setTierDialog(null); qc.invalidateQueries({ queryKey: ['loyalty-tiers'] }) }} />
      )}
      {bulkDialog && (
        <BulkCodesDialog tiers={tiers.filter((t) => t.isActive)}
          onClose={() => setBulkDialog(false)}
          onCreated={() => {
            setBulkDialog(false)
            qc.invalidateQueries({ queryKey: ['loyalty-tiers'] })
            qc.invalidateQueries({ queryKey: ['loyalty-codes'] })
          }} />
      )}
    </>
  )
}

// ============================================================
// Subcomponents
// ============================================================

function KpiCard({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider">
        {icon}<span>{label}</span>
      </div>
      <p className="text-2xl font-bold text-foreground mt-1">{value.toLocaleString('es-CO')}</p>
    </Card>
  )
}

function TabButton({ active, onClick, icon, label, count }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string; count: number }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
        active ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))]' : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}>
      {icon}{label}
      <span className={`text-xs px-1.5 py-0.5 rounded ${active ? 'bg-orange-100 text-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))] text-muted-foreground'}`}>{count}</span>
    </button>
  )
}

function EmptyState({ icon, title, description, action }: { icon: React.ReactNode; title: string; description: string; action?: React.ReactNode }) {
  return (
    <Card className="p-12 text-center border-dashed">
      <div className="flex justify-center mb-3">{icon}</div>
      <p className="font-semibold text-foreground mb-1">{title}</p>
      <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">{description}</p>
      {action}
    </Card>
  )
}

function TierCard({ tier, onEdit, onDelete }: { tier: Tier; onEdit: () => void; onDelete: () => void }) {
  return (
    <Card className="p-5 hover:border-border transition">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3">
          <div className="size-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tier.color + '20' }}>
            <Award className="h-5 w-5" style={{ color: tier.color }} />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{tier.name}</h3>
            <p className="text-xs text-muted-foreground font-mono">{tier.slug}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} className="p-1.5 hover:bg-[hsl(var(--muted))] rounded text-muted-foreground hover:text-muted-foreground"><Edit className="h-4 w-4" /></button>
          <button onClick={onDelete} className="p-1.5 hover:bg-red-50 rounded text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      {tier.description && <p className="text-sm text-muted-foreground mb-3">{tier.description}</p>}
      <div className="flex flex-wrap gap-2 mb-3">
        {tier.benefits.length === 0 ? (
          <span className="text-xs text-muted-foreground italic">sin beneficios configurados</span>
        ) : tier.benefits.map((b) => {
          const meta = BENEFIT_META[b.type]
          return (
            <span key={b.id} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-[hsl(var(--muted))] text-muted-foreground">
              {meta.icon}<span className="font-medium">{meta.label}:</span><span>{meta.describe(b.config)}</span>
            </span>
          )
        })}
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground pt-3 border-t border-gray-100">
        <span>{tier.defaultDurationDays ? `${tier.defaultDurationDays}d duración` : 'eterno'}</span>
        <span>·</span>
        <span>{tier.stackableWithCoupons ? 'stackable con cupones' : 'excluyente'}</span>
        <span>·</span>
        <span className={tier.isActive ? 'text-green-600' : 'text-muted-foreground'}>{tier.isActive ? 'activo' : 'inactivo'}</span>
      </div>
    </Card>
  )
}

function CodeRowComp({ code, onDelete }: { code: CodeRow; onDelete: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <tr className="hover:bg-[hsl(var(--muted))]">
      <td className="px-4 py-3">
        <button onClick={() => { navigator.clipboard.writeText(code.code); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
          className="font-mono text-sm font-medium text-foreground inline-flex items-center gap-2 hover:text-[hsl(var(--primary))]">
          {code.code}{copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3 text-muted-foreground" />}
        </button>
      </td>
      <td className="px-4 py-3 text-muted-foreground">{code.tierName}</td>
      <td className="px-4 py-3">
        <span className={`text-xs px-2 py-0.5 rounded font-medium ${
          code.status === 'redeemed' ? 'bg-green-100 text-green-700' :
          code.status === 'expired' ? 'bg-red-100 text-red-700' :
          'bg-blue-100 text-blue-700'
        }`}>
          {code.status === 'redeemed' ? 'Canjeado' : code.status === 'expired' ? 'Expirado' : 'Activo'}
        </span>
      </td>
      <td className="px-4 py-3 text-muted-foreground text-xs">{code.expiresAt ? fmtDate(code.expiresAt) : '—'}</td>
      <td className="px-4 py-3 text-muted-foreground text-xs">
        {code.redeemedByEmail ?? '—'}
        {code.redeemedAt && <span className="block text-[10px] text-muted-foreground">{fmtDate(code.redeemedAt)}</span>}
      </td>
      <td className="px-4 py-3 text-right">
        {code.status !== 'redeemed' && (
          <button onClick={onDelete} className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1">
            <X className="h-3 w-3" />Eliminar
          </button>
        )}
      </td>
    </tr>
  )
}

// ============================================================
// TierFormDialog
// ============================================================

function TierFormDialog({ mode, initialTier, onClose, onSaved }: {
  mode: 'create' | 'edit'
  initialTier?: Tier
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState(() => initialTier
    ? {
        id: initialTier.id, name: initialTier.name, slug: initialTier.slug,
        description: initialTier.description ?? '', color: initialTier.color,
        sortOrder: initialTier.sortOrder, defaultDurationDays: initialTier.defaultDurationDays,
        stackableWithCoupons: initialTier.stackableWithCoupons, isActive: initialTier.isActive,
        benefits: initialTier.benefits.map((b) => ({ type: b.type, config: b.config, isActive: b.isActive })),
      }
    : {
        name: '', slug: '', description: '', color: '#FFD700', sortOrder: 0,
        defaultDurationDays: null as number | null, stackableWithCoupons: true, isActive: true,
        benefits: [] as Array<{ type: BenefitType; config: Record<string, unknown>; isActive: boolean }>,
      },
  )
  const [error, setError] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)

  const mut = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name, slug: form.slug,
        description: form.description || null, color: form.color, sortOrder: form.sortOrder,
        defaultDurationDays: form.defaultDurationDays,
        stackableWithCoupons: form.stackableWithCoupons, isActive: form.isActive,
        benefits: form.benefits.map((b, i) => ({ type: b.type, config: b.config, sortOrder: i, isActive: b.isActive })),
      }
      if (mode === 'create') return tenantApi.post('/api/v1/loyalty/tiers', payload)
      return tenantApi.put(`/api/v1/loyalty/tiers/${(form as any).id}`, payload)
    },
    onSuccess: () => onSaved(),
    onError: (e: any) => setError(e?.message || 'Error al guardar'),
  })

  function addBenefit(type: BenefitType) {
    const meta = BENEFIT_META[type]
    setForm((f) => ({ ...f, benefits: [...f.benefits, { type, config: { ...meta.defaultConfig }, isActive: true }] }))
    setShowAdd(false)
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-hidden grid" style={{ gridTemplateRows: 'auto minmax(0, 1fr) auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold">{mode === 'create' ? 'Nuevo tier' : `Editar ${initialTier?.name}`}</h2>
        </div>

        <div className="overflow-y-auto px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, slug: mode === 'create' ? slugify(e.target.value) : f.slug }))} placeholder="Ej. Gold" />
            </div>
            <div>
              <Label>Slug *</Label>
              <Input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} pattern="[a-z0-9-]+" />
            </div>
          </div>

          <div>
            <Label>Descripción</Label>
            <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2}
              className="w-full px-3 py-2 border border-border rounded text-sm focus:outline-none focus:border-[hsl(var(--primary))] resize-none" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Color</Label>
              <div className="flex gap-2">
                <input type="color" value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} className="h-9 w-9 rounded border border-border cursor-pointer" />
                <Input value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} className="flex-1 font-mono text-xs" />
              </div>
            </div>
            <div>
              <Label>Orden</Label>
              <Input type="number" value={form.sortOrder} onChange={(e) => setForm((f) => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))} />
            </div>
            <div>
              <Label>Duración (días)</Label>
              <Input type="number" placeholder="Eterno" value={form.defaultDurationDays ?? ''} onChange={(e) => setForm((f) => ({ ...f, defaultDurationDays: e.target.value ? parseInt(e.target.value) : null }))} />
            </div>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.stackableWithCoupons} onChange={(e) => setForm((f) => ({ ...f, stackableWithCoupons: e.target.checked }))} className="h-4 w-4 rounded" />
              <span>Stackable con cupones</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4 rounded" />
              <span>Activo</span>
            </label>
          </div>

          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">Beneficios</h3>
              <div className="relative">
                <Button type="button" variant="outline" size="sm" onClick={() => setShowAdd((s) => !s)}>
                  <Plus className="h-3 w-3 mr-1" />Agregar<ChevronDown className="h-3 w-3 ml-1 opacity-60" />
                </Button>
                {showAdd && (
                  <div className="absolute right-0 mt-1 bg-white border border-border rounded shadow-lg z-10 w-56">
                    {(Object.keys(BENEFIT_META) as BenefitType[]).map((t) => (
                      <button key={t} onClick={() => addBenefit(t)} className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--muted))] flex items-center gap-2">
                        {BENEFIT_META[t].icon}{BENEFIT_META[t].label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {form.benefits.length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-3 text-center border border-dashed rounded">Sin beneficios. Agregá uno desde el botón.</p>
            ) : (
              <div className="max-h-[300px] overflow-y-auto space-y-2 pr-1">
                {form.benefits.map((b, idx) => (
                  <BenefitEditor key={idx} benefit={b}
                    onChange={(patch) => setForm((f) => ({ ...f, benefits: f.benefits.map((x, i) => i === idx ? { ...x, config: { ...x.config, ...patch } } : x) }))}
                    onRemove={() => setForm((f) => ({ ...f, benefits: f.benefits.filter((_, i) => i !== idx) }))} />
                ))}
              </div>
            )}
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</p>}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 bg-white">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !form.name || !form.slug}>
            {mut.isPending ? 'Guardando…' : (mode === 'create' ? 'Crear tier' : 'Guardar cambios')}
          </Button>
        </div>
      </div>
    </div>
  )
}

function BenefitEditor({ benefit, onChange, onRemove }: {
  benefit: { type: BenefitType; config: Record<string, unknown> }
  onChange: (patch: Record<string, unknown>) => void
  onRemove: () => void
}) {
  const meta = BENEFIT_META[benefit.type]
  const c = benefit.config

  return (
    <div className="border border-border rounded p-3 bg-[hsl(var(--muted))]">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground flex-1">
          {meta.icon}{meta.label}
        </div>
        <button type="button" onClick={onRemove} className="text-muted-foreground hover:text-red-600">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div>
        {benefit.type === 'discount_percent' && (
          <label className="text-xs text-muted-foreground flex items-center gap-2">
            <span className="w-32">Descuento %</span>
            <Input type="number" min={0} max={100} value={Number(c.value ?? 0)} onChange={(e) => onChange({ value: parseFloat(e.target.value) || 0 })} className="h-8 w-24" />
            <span>%</span>
          </label>
        )}
        {benefit.type === 'countdown_reduction' && (
          <label className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            <span className="w-32">Horas a reducir</span>
            <Input type="number" min={0} placeholder="ej. 24" value={(c.hours as number | undefined) ?? ''} onChange={(e) => onChange({ hours: e.target.value ? parseInt(e.target.value) : undefined, percent: undefined })} className="h-8 w-24" />
            <span>h</span>
            <span className="text-muted-foreground mx-1">o</span>
            <Input type="number" min={0} max={100} placeholder="ej. 50" value={(c.percent as number | undefined) ?? ''} onChange={(e) => onChange({ percent: e.target.value ? parseInt(e.target.value) : undefined, hours: undefined })} className="h-8 w-20" />
            <span>%</span>
          </label>
        )}
        {benefit.type === 'free_shipping' && <p className="text-xs text-muted-foreground">Envío sin costo, sin configuración adicional.</p>}
        {benefit.type === 'early_access' && (
          <label className="text-xs text-muted-foreground flex items-center gap-2">
            <span className="w-32">Acceso anticipado</span>
            <Input type="number" min={0} value={Number(c.hoursBeforeDrop ?? 12)} onChange={(e) => onChange({ hoursBeforeDrop: parseInt(e.target.value) || 0 })} className="h-8 w-24" />
            <span>horas antes del drop</span>
          </label>
        )}
        {benefit.type === 'category_discount' && (
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground flex items-center gap-2">
              <span className="w-32">Categoría (UUID)</span>
              <Input value={(c.categoryId as string) ?? ''} onChange={(e) => onChange({ categoryId: e.target.value })} placeholder="cat_..." className="h-8 flex-1 font-mono text-xs" />
            </label>
            <label className="text-xs text-muted-foreground flex items-center gap-2">
              <span className="w-32">Descuento</span>
              <Input type="number" min={0} max={100} value={Number(c.value ?? 0)} onChange={(e) => onChange({ value: parseFloat(e.target.value) || 0 })} className="h-8 w-24" />
              <span>%</span>
            </label>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================
// BulkCodesDialog
// ============================================================

interface TierLite { id: string; name: string; color: string }

function BulkCodesDialog({ tiers, onClose, onCreated }: { tiers: TierLite[]; onClose: () => void; onCreated: () => void }) {
  const [tierId, setTierId] = useState(tiers[0]?.id ?? '')
  const [count, setCount] = useState(10)
  const [expiresAt, setExpiresAt] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [generated, setGenerated] = useState<{ batchId: string; codes: Array<{ code: string; expiresAt: string | null; tierName: string }> } | null>(null)
  const [copiedAll, setCopiedAll] = useState(false)

  const mut = useMutation({
    mutationFn: () => tenantApi.post<{ batchId: string; codes: any[] }>('/api/v1/loyalty/codes/bulk', {
      tierId, count, expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
    }),
    onSuccess: (data) => setGenerated(data),
    onError: (e: any) => setError(e?.message || 'Error al generar'),
  })

  function handleClose() {
    if (generated) onCreated()
    else onClose()
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="bg-white rounded-lg max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">Generar códigos</h2>

        {!generated ? (
          <div className="space-y-4">
            <div>
              <Label>Tier</Label>
              <select value={tierId} onChange={(e) => setTierId(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded text-sm focus:outline-none focus:border-[hsl(var(--primary))]">
                <option value="">Elegí un tier</option>
                {tiers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {tiers.length === 0 && <p className="text-xs text-amber-600 mt-1">Creá al menos un tier activo antes.</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cantidad *</Label>
                <Input type="number" min={1} max={500} value={count} onChange={(e) => setCount(parseInt(e.target.value) || 1)} />
                <p className="text-[10px] text-muted-foreground mt-1">1 — 500 por lote</p>
              </div>
              <div>
                <Label>Expira (opcional)</Label>
                <Input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
                <p className="text-[10px] text-muted-foreground mt-1">Vacío = no expira</p>
              </div>
            </div>
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button onClick={() => mut.mutate()} disabled={mut.isPending || !tierId}>
                {mut.isPending ? 'Generando…' : `Generar ${count} códigos`}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded p-3 flex items-center gap-2">
              <Check className="h-4 w-4 text-green-600" />
              <p className="text-sm text-green-900">{generated.codes.length} códigos creados</p>
            </div>
            <div className="border border-border rounded max-h-72 overflow-y-auto bg-[hsl(var(--muted))] p-2">
              <div className="font-mono text-xs space-y-0.5">
                {generated.codes.map((c) => (
                  <div key={c.code} className="px-2 py-1 hover:bg-white rounded flex items-center gap-2">
                    <Ticket className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="font-medium text-foreground">{c.code}</span>
                    {c.expiresAt && <span className="text-muted-foreground text-[10px] ml-auto">vence {new Date(c.expiresAt).toLocaleDateString('es-CO')}</span>}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(generated.codes.map((c) => c.code).join('\n')); setCopiedAll(true); setTimeout(() => setCopiedAll(false), 1500) }} className="flex-1">
                {copiedAll ? <><Check className="h-4 w-4 mr-1.5" />Copiado</> : <><Copy className="h-4 w-4 mr-1.5" />Copiar todos</>}
              </Button>
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={handleClose}>Cerrar</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================
// Helpers
// ============================================================

function slugify(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '')
}
